import {withSystem, withMeta} from '@packages/systems/wfdl';

import {
  functionOf,
  TText,
  TList,
  TBoolean,
  TEnum,
  EFunction,
  EBoolean,
} from '@packages/systems/wfdl-ast';

import {USYS_ACCESS_TYPES} from '@packages/systems/users/constants';

import {LoginLogoutUserIcon} from '@packages/systems/spring';
// eslint-disable-next-line webflow/no-dangerous-imports
import {wrapAddPanelIcon} from '@packages/systems/ui/shared-ui/wrapAddPanelIcon';
import LogoutIcon from './icons/element-logout';
import AddPanelAccountSubscriptionListIcon from './icons/addpanel-account-subscription-list';
import AccountSubscriptionListIcon from './icons/element-account-subscription-list';

import UserAccountWrapper from './account/UserAccountWrapper';
import UserAccountFormWrapper, {
  UserAccountFormPreset,
} from './account/UserAccountFormWrapper';
import UserAccountForm from './account/UserAccountForm';
import UserAccountFormSaveButton from './account/UserAccountFormSaveButton';
import UserAccountFormCancelButton from './account/UserAccountFormCancelButton';
import UserAccountSubscriptionListWrapper from './account/UserAccountSubscriptionListWrapper';
import UserAccountSubscriptionListEmpty from './account/UserAccountSubscriptionListEmpty';
import UserAccountSubscriptionList from './account/UserAccountSubscriptionList';
import UserAccountSubscriptionListItem from './account/UserAccountSubscriptionListItem';
import UserAccountSubscriptionListItemInfo from './account/UserAccountSubscriptionListItemInfo';
import UserAccountSubscriptionCancelButton from './account/UserAccountSubscriptionCancelButton';

import UserSignUpFormWrapper, {
  UserSignUpFormPreset,
} from './signup/UserSignUpFormWrapper';
import UserSignUpForm from './signup/UserSignUpForm';
import UserSignUpVerificationMessage from './signup/UserSignUpVerificationMessage';
import UserSignUpErrorMsg from './signup/UserSignUpErrorMsg';
import UserSignUpTermsOfServiceWrapper from './signup/UserSignUpTermsOfServiceWrapper';
import UserSignUpTermsOfServiceCheckboxInput from './signup/UserSignUpTermsOfServiceCheckboxInput';
import UserSignUpRedirectWrapper from './signup/UserSignUpRedirectWrapper';

import UserResetPasswordFormWrapper, {
  UserResetPasswordFormPreset,
} from './resetPassword/UserResetPasswordFormWrapper';
import UserResetPasswordForm from './resetPassword/UserResetPasswordForm';
import UserResetPasswordErrorMsg from './resetPassword/UserResetPasswordErrorMsg';

import UserLogInFormWrapper, {
  UserLogInFormPreset,
} from './login/UserLogInFormWrapper';
import UserLogInForm from './login/UserLogInForm';
import UserLogInErrorMsg from './login/UserLogInErrorMsg';

import UserUpdatePasswordFormWrapper, {
  UserUpdatePasswordFormPreset,
} from './updatePassword/UserUpdatePasswordFormWrapper';
import UserUpdatePasswordForm from './updatePassword/UserUpdatePasswordForm';
import UserUpdatePasswordErrorMsg from './updatePassword/UserUpdatePasswordErrorMsg';

import UserFormEmailInput from './UserFormEmailInput';
import UserFormNameInput from './UserFormNameInput';
import UserFormPasswordInput from './UserFormPasswordInput';
import UserFormButton from './UserFormButton';
import UserFormBlockLabel from './UserFormBlockLabel';
import UserFormErrorState from './UserFormErrorState';
import UserFormErrorStateStyleVariant1 from './UserFormErrorStateStyleVariant1';
import UserFormSuccessState from './UserFormSuccessState';

import UserFormHeader from './layout/UserFormHeader';
import UserFormFooter from './layout/UserFormFooter';
import UserFormPageWrap from './layout/UserFormPageWrap';
import BlockContent from './layout/BlockContent';
import BlockHeader from './layout/BlockHeader';
import FlexColumn from './layout/FlexColumn';
import GridRow from './layout/GridRow';

import UserLogOutLogIn from './logout/UserLogOutLogIn';
import UserErrorMsgType from './datatypes/UserErrorMsgType';
import UserErrorMsg from './UserErrorMsg';
import UserFormType from './datatypes/UserFormType';
import UserFormTextInput from './form/UserFormTextInput';
import UserFormNumberInput from './form/UserFormNumberInput';
import UserFormCheckboxInput from './form/UserFormCheckboxInput';
import UserFormErrorStateChildrenType from './datatypes/UserFormErrorStateChildrenType';
import UserFormFileUploadInput from './UserFormFileUploadInput';
import UserFormUploadName from './UserFormUploadName';
import UserFormSelect from './UserFormSelect';

import AccessGroupSelectType from './datatypes/AccessGroupSelectType';

export const templatePresets = {
  signup: UserSignUpFormPreset,
  login: UserLogInFormPreset,
  reset: UserResetPasswordFormPreset,
  update: UserUpdatePasswordFormPreset,
  account: UserAccountFormPreset,
} as const;

const AddPanelLogoutIcon = wrapAddPanelIcon(LoginLogoutUserIcon);

export const Users = {
  icons: {
    // account
    AddPanelAccountSubscriptionList: AddPanelAccountSubscriptionListIcon,
    AccountSubscriptionList: AccountSubscriptionListIcon,
    // logout
    AddPanelLogout: AddPanelLogoutIcon,
    Logout: LogoutIcon,
  },
  atoms: {
    // account
    UserAccountWrapper,
    UserAccountFormWrapper,
    UserAccountForm,
    UserAccountFormSaveButton,
    UserAccountFormCancelButton,
    UserAccountSubscriptionListWrapper,
    UserAccountSubscriptionListEmpty,
    UserAccountSubscriptionList,
    UserAccountSubscriptionListItem,
    UserAccountSubscriptionListItemInfo,
    UserAccountSubscriptionCancelButton,
    // sign up
    UserSignUpFormWrapper,
    UserSignUpForm,
    UserSignUpVerificationMessage,
    UserSignUpRedirectWrapper,
    // legacy (closed beta)
    UserSignUpTermsOfServiceWrapper,
    // legacy (closed beta)
    UserSignUpTermsOfServiceCheckboxInput,
    // log in
    UserLogInFormWrapper,
    UserLogInForm,
    // update password
    UserUpdatePasswordFormWrapper,
    UserUpdatePasswordForm,
    // reset password
    UserResetPasswordFormWrapper,
    UserResetPasswordForm,
    // log out/log in
    UserLogOutLogIn,
    // common user form atoms
    UserFormBlockLabel,
    UserFormButton,
    UserFormCheckboxInput,
    UserFormTextInput,
    UserFormEmailInput,
    UserFormNameInput,
    UserFormPasswordInput,
    UserFormSelect,
    UserFormNumberInput,
    UserFormFileUploadInput,
    UserFormUploadName,
    // layout for template pages
    UserFormHeader,
    UserFormFooter,
    UserFormPageWrap,
    BlockContent,
    BlockHeader,
    FlexColumn,
    GridRow,
    // success state
    UserFormSuccessState,
    // error states
    UserFormErrorState,
    UserFormErrorStateStyleVariant1,
    UserLogInErrorMsg,
    UserSignUpErrorMsg,
    UserResetPasswordErrorMsg,
    UserUpdatePasswordErrorMsg,
    UserErrorMsg,
  },
  dataTypes: {
    UserForm: UserFormType,
    UserFormErrorStateChildren: UserFormErrorStateChildrenType,
    UserErrorMsg: UserErrorMsgType,
    AccessGroupSelect: AccessGroupSelectType,
  },
  elementPresets: {},
  values: {
    isUserLoggedIn: {
      envType: {
        dataType: withSystem(
          {label: 'User state', isBindable: false, hiddenProperty: true},
          functionOf(
            withMeta(
              {variant: 'menu'},
              TEnum({
                eq: 'is Logged In',
                ne: 'is Logged Out',
              })
            ),
            TBoolean
          )
        ),
        exported: 'no',
      },
      expression: EFunction({
        param: '_',
        body: EBoolean(false),
      }),
    },
    computeIsInAccessGroup: {
      envType: {
        dataType: functionOf(
          TList(TText),
          functionOf(
            withMeta(
              {variant: 'menu'},
              TEnum({
                eq: 'is',
                ne: 'is not',
              })
            ),
            functionOf(TText, TBoolean)
          )
        ),
        exported: 'no',
      },
      value:
        (accessGroupsOfUser: string[]) =>
        (selectedEquality: 'eq' | 'ne') =>
        (selectedAccessGroup: string) => {
          if (
            accessGroupsOfUser.includes(USYS_ACCESS_TYPES.ADMIN_ALWAYS_VISIBLE)
          ) {
            return true;
          }
          const accessGroupMatches =
            accessGroupsOfUser.includes(selectedAccessGroup);
          if (selectedEquality === 'eq') {
            return accessGroupMatches;
          }
          if (selectedEquality === 'ne') {
            return !accessGroupMatches;
          }
        },
    },
  },
} as const;
